import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Upload,
  X,
  FileImage,
  FileVideo,
  FileText,
  File,
  Figma,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';

const FILE_TYPE_ICONS = {
  image: FileImage,
  video: FileVideo,
  document: FileText,
  design: Figma,
  unknown: File
};

const ALLOWED_EXTENSIONS = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
  video: ['mp4', 'mov', 'webm', 'avi'],
  document: ['pdf', 'doc', 'docx', 'pptx', 'txt']
};

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

const getFileType = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  for (const [type, extensions] of Object.entries(ALLOWED_EXTENSIONS)) {
    if (extensions.includes(ext)) return type;
  }
  return 'unknown';
};

const isAllowedFile = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  const allAllowed = Object.values(ALLOWED_EXTENSIONS).flat();
  return allAllowed.includes(ext);
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileUpload = ({ api, onUploadComplete, onError }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const validateFile = (file) => {
    if (!file) return 'No file selected';
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}`;
    }
    if (!isAllowedFile(file.name)) {
      return 'File type not allowed. Supported: images, videos, PDFs, and documents';
    }
    return null;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    const error = validateFile(file);
    
    if (error) {
      setErrorMessage(error);
      setUploadStatus('error');
      return;
    }
    
    setSelectedFile(file);
    setErrorMessage('');
    setUploadStatus('idle');
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    const error = validateFile(file);
    
    if (error) {
      setErrorMessage(error);
      setUploadStatus('error');
      return;
    }
    
    setSelectedFile(file);
    setErrorMessage('');
    setUploadStatus('idle');
  };

  const uploadChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = crypto.randomUUID();
    
    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
      const start = chunkNumber * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('chunk_number', chunkNumber.toString());
      formData.append('total_chunks', totalChunks.toString());
      formData.append('upload_id', uploadId);
      formData.append('filename', file.name);
      
      const response = await api.post('/upload/chunk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadProgress(Math.round(((chunkNumber + 1) / totalChunks) * 100));
      
      if (response.data.completed) {
        return response.data;
      }
    }
    
    throw new Error('Upload failed: Not all chunks received');
  };

  const uploadSimple = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setUploadProgress(progress);
      }
    });
    
    return response.data;
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploadStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    
    try {
      let result;
      
      // Use chunked upload for files > 50MB
      if (selectedFile.size > 50 * 1024 * 1024) {
        result = await uploadChunked(selectedFile);
      } else {
        result = await uploadSimple(selectedFile);
      }
      
      setUploadStatus('success');
      onUploadComplete?.(result);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setErrorMessage(error.response?.data?.detail || error.message || 'Upload failed');
      onError?.(error);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fileType = selectedFile ? getFileType(selectedFile.name) : 'unknown';
  const FileIcon = FILE_TYPE_ICONS[fileType];

  return (
    <div className="space-y-4">
      {/* Drag & Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-300 hover:border-slate-400'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-testid="file-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleFileSelect}
          accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.mov,.webm,.avi,.pdf,.doc,.docx,.pptx,.txt"
          data-testid="file-input"
        />
        
        {!selectedFile ? (
          <div className="space-y-3">
            <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-slate-400" />
            </div>
            <div>
              <p className="text-slate-700 font-medium">
                Drag & drop your file here, or{' '}
                <span className="text-blue-600">browse</span>
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Images, videos, PDFs, and documents up to 200MB
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileIcon className="w-6 h-6 text-slate-500" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="font-medium text-slate-900 truncate">{selectedFile.name}</p>
              <p className="text-sm text-slate-500">
                {formatFileSize(selectedFile.size)} • {fileType}
              </p>
            </div>
            {uploadStatus === 'idle' && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={(e) => { e.stopPropagation(); handleReset(); }}
                className="flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {uploadStatus === 'uploading' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Uploading...</span>
            <span className="text-slate-900 font-medium">{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}

      {/* Success Message */}
      {uploadStatus === 'success' && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg text-emerald-700">
          <Check className="w-5 h-5" />
          <span>File uploaded successfully!</span>
        </div>
      )}

      {/* Error Message */}
      {uploadStatus === 'error' && errorMessage && (
        <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Upload Button */}
      {selectedFile && uploadStatus === 'idle' && (
        <Button 
          className="w-full" 
          onClick={handleUpload}
          data-testid="upload-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload File
        </Button>
      )}

      {/* Reset Button after success/error */}
      {(uploadStatus === 'success' || uploadStatus === 'error') && (
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={handleReset}
          data-testid="reset-upload-btn"
        >
          Upload Another File
        </Button>
      )}
    </div>
  );
};

export const FigmaLinkInput = ({ value, onChange }) => {
  const [error, setError] = useState('');

  const validateFigmaUrl = (url) => {
    if (!url) return true;
    const figmaPattern = /^https:\/\/(www\.)?figma\.com\/(file|design|proto)\/.+/;
    return figmaPattern.test(url);
  };

  const handleChange = (e) => {
    const url = e.target.value;
    if (url && !validateFigmaUrl(url)) {
      setError('Please enter a valid Figma URL');
    } else {
      setError('');
    }
    onChange(url);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 p-3 border rounded-lg">
        <Figma className="w-5 h-5 text-slate-400" />
        <input
          type="url"
          placeholder="https://www.figma.com/file/..."
          value={value || ''}
          onChange={handleChange}
          className="flex-1 outline-none text-sm"
          data-testid="figma-url-input"
        />
      </div>
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      <p className="text-xs text-slate-500">
        Paste a Figma file, prototype, or design URL
      </p>
    </div>
  );
};
